import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

interface CreateBucketModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (bucketName: string) => void;
}

const CreateBucketModal: React.FC<CreateBucketModalProps> = ({
    isOpen,
    onClose,
    onCreate
}) => {
    const [bucketName, setBucketName] = useState('');
    const [error, setError] = useState('');

    const validateBucketName = (name: string): string | null => {
        if (!name.trim()) {
            return 'Bucket name cannot be empty';
        }
        if (name.length < 3 || name.length > 63) {
            return 'Bucket name must be between 3 and 63 characters long';
        }
        const bucketNameRegex = /^[a-z0-9][a-z0-9.\-]{1,61}[a-z0-9]$/;
        if (!bucketNameRegex.test(name)) {
            return 'Bucket name must start and end with a letter or number, and can only contain lowercase letters, numbers, dots, and hyphens';
        }
        const ipv4Regex = /^(?:\d{1,3}\.){3}\d{1,3}$/;
        if (ipv4Regex.test(name)) {
            return 'Bucket name must not be formatted as an IP address';
        }
        if (name.includes('..') || name.includes('.-') || name.includes('-.')) {
            return 'Bucket name cannot contain consecutive dots or mixed dot-hyphen sequences';
        }
        return null;
    };

    const handleCreate = () => {
        const validationError = validateBucketName(bucketName);
        if (validationError) {
            setError(validationError);
            return;
        }
        onCreate(bucketName);
        setBucketName('');
        setError('');
        onClose();
    };

    const handleClose = () => {
        setBucketName('');
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Create New Bucket</h3>
                    <button
                        onClick={handleClose}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bucket Name
                    </label>
                    <input
                        type="text"
                        value={bucketName}
                        onChange={(e) => {
                            setBucketName(e.target.value.toLowerCase());
                            setError('');
                        }}
                        placeholder="my-bucket-name"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                    {error && (
                        <div className="flex items-start gap-2 mt-2 text-red-600">
                            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                        Bucket names must be 3-63 characters, lowercase, and can only contain letters, numbers, dots, and hyphens.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition-colors"
                    >
                        Create Bucket
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateBucketModal;
